import {Injectable} from '@angular/core';
import {BehaviorSubject, from} from 'rxjs';
import {map, switchMap, take, tap} from 'rxjs/operators';
import {CommonUtils} from '../utils/common';
import {NavigationExtras, Router} from '@angular/router';
import {UserAuthData} from "../models/user-auth-data.model";
import jwtDecode from "jwt-decode";
import {fromPromise} from "rxjs/internal-compatibility";

/**
 * Główny serwis odpowiedzialny za uwierzytelnianie użytkowników w aplikacji.
 * Zarządza stanem sesji, przechowuje i dekoduje tokeny oraz synchronizuje
 * stan logowania z lokalną pamięcią urządzenia (Storage).
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _userAuthData = new BehaviorSubject<UserAuthData>(null);

  constructor(
    public commonUtils: CommonUtils,
    private router: Router
  ) {
  }

  /**
   * Zwraca bezpośredni strumień obserwowalny (Observable) zawierający bieżące dane autoryzacyjne.
   */
  get userAuthData() {
    return this._userAuthData.asObservable();
  }

  /**
   * Zwraca strumień danych autoryzacyjnych, implementując mechanizm Lazy Loading.
   * Jeśli dane nie znajdują się w pamięci RAM (BehaviorSubject), asynchronicznie
   * pobiera je z pamięci lokalnej urządzenia i aktualizuje strumień.
   *
   * @returns Observable emitujący obiekt UserAuthData.
   */
  getUserAuthData() {
    if (this._userAuthData.getValue()) {
      console.log('uad from bs')
      return this._userAuthData.asObservable();
    } else {
      return fromPromise(this.commonUtils.getStorageObject<UserAuthData>('userAuthData').then(async (userAuthData) => {
        return userAuthData;
      })).pipe(take(1), switchMap(userAuthData => {
        if (userAuthData) {
          console.log('uad from ls')
          this._userAuthData.next(userAuthData);
          return this._userAuthData;
        } else {
          return this._userAuthData;
        }
      }));
    }
  }

  /**
   * Aktualizuje stan autoryzacji w pamięci tymczasowej.
   * Automatycznie odczytuje i przypisuje role zdekodowane z tokenu JWT przed emisją.
   *
   * @param newValue Nowy obiekt danych autoryzacyjnych.
   */
  updateUserAuthData(newValue: UserAuthData) {
    this.decodedToken.pipe(take(1)).subscribe(dToken => {
      if (dToken && dToken.$int_roles) {
        newValue.roles = dToken.$int_roles;
      }
      this._userAuthData.next(newValue);
    })
  }

  /**
   * Zwraca strumień logiczny określający, czy użytkownik jest aktualnie zalogowany.
   * Sprawdza fizyczną obecność tokenu w obiekcie sesji.
   */
  get userIsAuthenticated() {
    return this.userAuthData.pipe(
      map(userAuthData => {
        if (userAuthData) {
          return !!userAuthData.token;
        } else {
          return false;
        }
      })
    );
  }

  /**
   * Wyciąga i zwraca sam ciąg znaków tokenu JWT z bieżącego stanu sesji.
   */
  get token() {
    return this.userAuthData.pipe(
      map(userAuthData => {
        if (userAuthData) {
          return userAuthData.token;
        } else {
          return null;
        }
      })
    );
  }

  /**
   * Zwraca zdekodowany ładunek (Payload) tokenu JWT.
   */
  get decodedToken(): any {
    return this.userAuthData.pipe(
      map(userAuthData => {
        if (userAuthData) {
          return jwtDecode(userAuthData.token);
        } else {
          return null;
        }
      })
    );
  }

  /**
   * Próbuje automatycznie zalogować użytkownika przy starcie aplikacji na podstawie
   * danych zapisanych w pamięci lokalnej. Jeśli token nie istnieje, przekierowuje
   * do ekranu logowania.
   *
   * @returns Observable emitujący wartość logiczną (true jeśli zalogowano, false w przeciwnym razie).
   */
  autoLogin() {
    return from(this.commonUtils.getStorageObject<UserAuthData>('userAuthData')).pipe(
      map(userAuthData => {
        if (!userAuthData || !userAuthData.token) {
          return null;
        }
        return userAuthData;
      }),
      tap(userAuthData => {
        if (userAuthData) {
          this._userAuthData.next(userAuthData);
        }
      }),
      map(userAuthData => {
        if (userAuthData === null) this.router.navigate(['signin']);
        return !!userAuthData;
      })
    );
  }

  /**
   * Wykonuje próbę automatycznego logowania, z możliwością przekazania parametrów
   * nawigacyjnych (np. Return URL), aby po udanym zalogowaniu odesłać użytkownika
   * na stronę, którą pierwotnie chciał odwiedzić.
   *
   * @param params Opcje nawigacji przekazywane do routera.
   */
  autoLoginWithReturnUrl(params: NavigationExtras) {
    return from(this.commonUtils.getStorageObject<UserAuthData>('userAuthData')).pipe(
      map(userAuthData => {
        if (!userAuthData || !userAuthData.token) {
          return null;
        }
        return userAuthData;
      }),
      tap(userAuthData => {
        if (userAuthData) {
          this._userAuthData.next(userAuthData);
        }
      }),
      map(userAuthData => {
        if (userAuthData === null) this.router.navigate(['signin'], params);
        return !!userAuthData;
      })
    );
  }

  /**
   * Kompletny proces logowania - ustawia nowy stan autoryzacji, dekoduje role z tokenu JWT
   * i trwale zapisuje dane sesji w pamięci urządzenia.
   *
   * @param userAuthData Obiekt sesji otrzymany z backendu po poprawnym uwierzytelnieniu.
   */
  setUserAuthData(userAuthData: UserAuthData) {
    this.decodedToken.pipe(take(1)).subscribe(dToken => {
      if (dToken && dToken.$int_roles) {
        userAuthData.roles = dToken.$int_roles;
      }
      this._userAuthData.next(userAuthData);
      this.storeUserAuthData(userAuthData);
    })
  }

  /**
   * Kończy sesję użytkownika. Czyści stan aplikacji w pamięci RAM, usuwa klucze z pamięci
   * lokalnej i wymusza twarde przeładowanie strony w celu wyczyszczenia pozostałości.
   */
  logout() {
    this.commonUtils.removeStorageObject('userAuthData').then(() => {
      this.commonUtils.removeStorageObject('user').then(() => {
        window.location.reload();
      });
    });
  }

  /**
   * Zapisuje dane autoryzacyjne w trwałej pamięci urządzenia.
   *
   * @param userAuthData Obiekt do zapisania.
   */
  private storeUserAuthData(userAuthData: UserAuthData) {
    this.commonUtils.setStorageObject('userAuthData', userAuthData);
  }
}
